/**
 * The plan's fourth source: METAR and TAF.
 *
 * Same shape as `route.ts` and `aircraft.ts` — the rules are pure functions over
 * values, so they are pinned down by tests instead of by clicking, and the store
 * owns *when* they run and which cells they may touch (`PlanStore.setWx`).
 * The one impure function is {@link loadWeather}: it asks `GET /api/wx/metar`,
 * maps the answer through the pure half and hands the result to the store.
 *
 * What this fills, and nothing else:
 *   · the ATIS / Platzwetter rows (B6) — station, time, wind, visibility,
 *     clouds, temp/dewpoint, QNH, trend, from `@avia/core`'s `parseMetarToAtis`
 *   · the Wetter-Beratung lines (V6) — the raw TAF and METAR text, written by the
 *     same pass and once per bulletin ({@link autoWetterLines})
 *
 * It deliberately does NOT touch the route's W/V column: the wind in a METAR is
 * the surface wind at one aerodrome, and writing it into a cruise leg would be a
 * navigation error wearing the clothes of a measurement. That column has its own
 * source and its own endpoint — `winds.ts`.
 *
 * Where there is no METAR the app says so, names the NEAREST station that does
 * report (`GET /api/wx/nearby`, see {@link loadNearby}), and points at the
 * OFFICIAL self-briefing service for that country's prefix — as a link the pilot
 * follows. None of those sites is ever fetched or scraped from here: they are
 * AIS/met services with their own terms, and a flight plan that silently
 * re-published their pages would be neither legal nor trustworthy.
 *
 * The nearby half has one rule above all the others: **a neighbour's report may
 * never be readable as this aerodrome's own.** It is shown as the neighbour's, and
 * the cell it fills says whose report it is and how far away ("LJLJ (21 NM von
 * LJCE)"). Everything else in this file follows from that sentence — including the
 * fact that the unattended pass writes it into a FREE row only, and never into one
 * carrying an observation somebody asked for ({@link nearbyRows}).
 */

import { AIRFIELDS, parseMetarToAtis, type AtisRow } from '@avia/core';

import {
  fetchMetar, fetchNearby, isAbort,
  type WxMetar, type WxNearbyStation, type WxResponse, type WxStation,
} from './api';
import { airportLatLon } from './geo';
import type { PlanStore, WxNearbyStatus, WxStatus, WxStationStatus } from './store';

/** The endpoint's own attribution string; the fallback when a stored plan has none. */
export const WX_SOURCE = 'NOAA aviationweather.gov';

/** Only a real ICAO location indicator can be asked for — the server rejects anything else. */
export const ICAO_RE = /^[A-Z]{4}$/;

/**
 * The ATIS rows the paper form has. The LBA form's B6 block is two rows tall
 * (`packages/core/src/pdf/lba-fields.json` holds `atis1_*` and `atis2_*` and
 * nothing beyond), so a third station is fetched and reported but has nowhere to
 * be printed — the pilot is told, rather than silently losing it.
 */
export const ATIS_ROWS: readonly number[] = [1, 2];

/** The cells of one ATIS row a METAR can answer for. */
const ATIS_CELLS = ['station', 'zeit', 'wind', 'sicht', 'wolken', 'temp', 'qnh', 'tendenz'] as const;

/** The Wetter-Beratung block (V6) — six free lines. */
export const WETTER_LINES = 6;

/**
 * How far the fallback looks, and how many neighbours it names.
 *
 * 60 NM is roughly half an hour in a PA-28 and is what a briefing service offers for
 * an aerodrome with no report of its own. **One** station, not the endpoint's default
 * two: the paper form has two ATIS rows in total and the plan usually wants both of
 * them, so a menu of neighbours would be a choice the form has no room to record.
 */
export const NEARBY_RADIUS_NM = 60;
export const NEARBY_LIMIT = 1;

export const atisField = (row: number, key: string): string => 'atis' + row + '_' + key;
export const wetterField = (line: number): string => 'wetter_' + line;

/* ---------------- which stations ---------------- */

/**
 * The stations a plan is about: departure, destination and alternate, in that
 * order, de-duplicated, and only where the cell really holds a location
 * indicator (`von` may well say "Platzrunde").
 */
export function planStations(val: (id: string) => string): string[] {
  const out: string[] = [];
  for (const id of ['von', 'nach', 'alt_name']) {
    const v = val(id).trim().toUpperCase();
    if (ICAO_RE.test(v) && !out.includes(v)) out.push(v);
  }
  return out;
}

/* ---------------- official self-briefing ---------------- */

/** Where to brief officially when we have no METAR — a link, never a fetch. */
export interface BriefingLink {
  service: string;
  url: string;
}

/**
 * The AIS / met service for an ICAO prefix. Only the three the app's own users
 * fly in are listed; anything else gets the generic sentence rather than a guess,
 * because sending a pilot to the wrong country's briefing is worse than sending
 * them to none.
 */
export function briefingFor(icao: string): BriefingLink | null {
  const id = icao.toUpperCase();
  if (id.startsWith('LJ')) return { service: 'ARSO/AIS', url: 'https://meteo.arso.gov.si' };
  if (id.startsWith('LO')) return { service: 'Austro Control', url: 'https://www.austrocontrol.at' };
  if (id.startsWith('ED') || id.startsWith('ET')) {
    return { service: 'DWD', url: 'https://www.dwd.de/DE/fachnutzer/luftfahrt' };
  }
  return null;
}

/* ---------------- times ---------------- */

const finiteDate = (ms: number): Date | null => {
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * A time as the API gave it → a Date. The endpoint emits ISO-8601 UTC; epoch
 * seconds are accepted as well, because that is what NOAA itself publishes and
 * what an older build of the endpoint may have written into a stored `meta.wx`.
 */
function toDate(v: string | number | null | undefined): Date | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return finiteDate(v * 1000);
  if (/^\d{9,}$/.test(v)) return finiteDate(Number(v) * 1000);
  return finiteDate(Date.parse(v));
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** "10:20Z" — the way an observation time is written. '' when unknown. */
export function zTime(v: string | number | null | undefined): string {
  const d = toDate(v);
  return d ? pad2(d.getUTCHours()) + ':' + pad2(d.getUTCMinutes()) + 'Z' : '';
}

/** "18Z" — a TAF's validity is published on the whole hour. '' when unknown. */
export function zHour(v: string | number | null | undefined): string {
  const d = toDate(v);
  return d ? pad2(d.getUTCHours()) + 'Z' : '';
}

/* ---------------- flight category ---------------- */

/**
 * The four categories a briefing colour-codes by. They are international codes and
 * are printed as they stand — VFR/MVFR/IFR/LIFR are not translated any more than
 * BKN or TEMPO is.
 */
export type FlightCategory = 'VFR' | 'MVFR' | 'IFR' | 'LIFR';

/**
 * NOAA's visibility, in **statute miles**, as a number.
 *
 * The field is dynamically typed upstream and every one of these really occurs:
 * `4`, `2.49`, the string `"6+"` ("more than six", i.e. 9999 or CAVOK), and the
 * EMPTY STRING for a forecast period that states no visibility at all. `"6+"`
 * becomes 6 — the category boundaries are all below it, so the plus adds nothing
 * a colour could show — and an unstated visibility is `null`, not zero.
 */
export function visibSm(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;
  const t = v.trim();
  if (t === '') return null;
  const n = Number.parseFloat(t.replace('+', ''));
  return Number.isFinite(n) ? n : null;
}

/** `"6+"` — "more than this", not "this". The line has to say so, or it overstates. */
export function visibIsPlus(v: unknown): boolean {
  return typeof v === 'string' && v.trim().endsWith('+');
}

/**
 * The visibility as a European pilot reads it: metres up to 5 km, kilometres
 * above.
 *
 * The decoding upstream is in statute miles, which is the unit NOAA writes and
 * not one anybody plans VFR in here — SERA's minima are in km and m, and no
 * other number in this app is US-only. Converting back cannot restore what the
 * decoding lost (a TAF's `4000` arrives as `2.49`), so the precision is thrown
 * away openly instead of pretended at: whole hundreds of metres below 5 km,
 * whole kilometres above. `2.49` becomes „4000 m", which is what the TAF said.
 */
export function visibMetric(sm: number): string {
  const m = sm * 1609.344;
  if (m >= 5000) return String(Math.round(m / 1000)) + ' km';
  return String(Math.max(0, Math.round(m / 100)) * 100) + ' m';
}

/**
 * The ceiling of a decoded cloud list, in feet AGL: the lowest BKN or OVC layer.
 *
 * FEW and SCT are not a ceiling — that is the definition, not a simplification —
 * so a sky of SCT010 is VFR by ceiling and only the visibility can downgrade it.
 * `null` is "no ceiling reported", which is what an unlimited sky looks like and
 * is therefore treated as no restriction rather than as zero.
 */
export function ceilingFt(clouds: unknown): number | null {
  if (!Array.isArray(clouds)) return null;
  let low: number | null = null;
  for (const c of clouds) {
    if (typeof c !== 'object' || c === null) continue;
    const o = c as { cover?: unknown; base?: unknown; base_ft?: unknown };
    const cover = typeof o.cover === 'string' ? o.cover.toUpperCase() : '';
    // OVX is an obscured sky with a vertical visibility instead of a base; it is a
    // ceiling by definition and its height arrives in `vertVis`, handled by the caller.
    if (cover !== 'BKN' && cover !== 'OVC' && cover !== 'OVX') continue;
    const raw = typeof o.base_ft === 'number' ? o.base_ft : o.base;
    if (typeof raw !== 'number' || !Number.isFinite(raw)) continue;
    if (low === null || raw < low) low = raw;
  }
  return low;
}

/**
 * The standard flight category from a ceiling and a visibility:
 *
 * | | ceiling (ft AGL) | visibility (SM) |
 * |---|---|---|
 * | LIFR | < 500 | < 1 |
 * | IFR | < 1 000 | < 3 |
 * | MVFR | < 3 000 | < 5 |
 * | VFR | ≥ 3 000 | ≥ 5 |
 *
 * The **worse** of the two decides, which is why each row is an OR. Either may be
 * `null` ("not reported"), and a `null` is not a restriction: an unreported ceiling
 * is an unlimited sky, and that is exactly how a TEMPO group that names only a
 * visibility has to be read. Both `null` is `null` — there is nothing to colour.
 *
 * This is the US NWS definition, which NOAA's own `fltCat` uses, and it is what the
 * dots on a briefing mean everywhere they are drawn. It is **not** a legal VMC test:
 * European VFR minima depend on airspace class and on altitude, and no colour on a
 * screen has ever been a clearance.
 */
export function flightCategory(ceiling: number | null, visib: number | null): FlightCategory | null {
  if (ceiling === null && visib === null) return null;
  const c = ceiling ?? Infinity;
  const v = visib ?? Infinity;
  if (c < 500 || v < 1) return 'LIFR';
  if (c < 1000 || v < 3) return 'IFR';
  if (c < 3000 || v < 5) return 'MVFR';
  return 'VFR';
}

/**
 * The category of an observation. NOAA computes one (`fltCat`) and it is taken
 * verbatim where it exists — it is the publisher's own reading of its own report,
 * and a second implementation that disagreed by one foot of cloud base would be a
 * bug with no way to tell which half was wrong. Only where NOAA published none do
 * we derive it, from the same two fields.
 */
export function metarCategory(m: WxMetar | null | undefined): FlightCategory | null {
  if (!m) return null;
  const given = (m.fltcat ?? '').toUpperCase();
  if (given === 'VFR' || given === 'MVFR' || given === 'IFR' || given === 'LIFR') return given;
  return flightCategory(ceilingFt(m.clouds), visibSm(m.visib));
}

/* ---------------- TAF periods ---------------- */

/**
 * One decoded forecast period of a TAF, reduced to what a coloured line shows.
 *
 * NOAA publishes `fltCat` for an observation and **not** for a forecast period, so
 * unlike {@link metarCategory} this one is always ours — which is also why the
 * numbers it was derived from are carried along and rendered: a dot nobody can check
 * is a decoration.
 *
 * `visib` is carried in NOAA's **statute miles**, because that is what arrives,
 * and shown in metres — see {@link visibMetric} for what that conversion may and
 * may not claim. `visibPlus` keeps the one thing the number alone cannot say:
 * that the group was `9999` / CAVOK / `P6SM` and means "at least".
 */
export interface TafPeriod {
  /** ISO-8601 or NOAA's epoch seconds, whichever the endpoint passed through */
  from: string | number | null;
  to: string | number | null;
  /** `TEMPO` / `BECMG` / `FM` … as NOAA spells it, or '' for the prevailing group */
  change: string;
  /** `30` for a PROB30 group, else null */
  probability: number | null;
  category: FlightCategory | null;
  /** statute miles — see above */
  visib: number | null;
  /** the group said "more than this" (`9999`, CAVOK, `P6SM`), not "this" */
  visibPlus: boolean;
  ceilingFt: number | null;
  /** the significant weather string, e.g. `TSRA`; '' when the period names none */
  wx: string;
}

const numOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

/**
 * NOAA's `fcsts` array → the periods a briefing draws.
 *
 * It is a passthrough on the server (`wx.rs` never re-encodes it), so it is `unknown`
 * here and every field is checked. A period with no times at all is dropped: a
 * coloured bar with nothing to say when it applies is worse than one fewer bar.
 */
export function tafPeriods(fcsts: unknown): TafPeriod[] {
  if (!Array.isArray(fcsts)) return [];
  const out: TafPeriod[] = [];
  for (const f of fcsts) {
    if (typeof f !== 'object' || f === null) continue;
    const o = f as Record<string, unknown>;
    const from = (typeof o['timeFrom'] === 'number' || typeof o['timeFrom'] === 'string') ? o['timeFrom'] : null;
    const to = (typeof o['timeTo'] === 'number' || typeof o['timeTo'] === 'string') ? o['timeTo'] : null;
    if (from === null && to === null) continue;
    // an obscured sky reports a vertical visibility instead of a cloud base; it is a
    // ceiling, and dropping it would colour a 200 ft fog bank as VFR
    const vv = numOrNull(o['vertVis']);
    const ceil = ceilingFt(o['clouds']);
    const ceiling = vv === null ? ceil : ceil === null ? vv : Math.min(vv, ceil);
    const visib = visibSm(o['visib']);
    out.push({
      from,
      to,
      change: typeof o['fcstChange'] === 'string' ? o['fcstChange'] : '',
      probability: numOrNull(o['probability']),
      category: flightCategory(ceiling, visib),
      visib,
      visibPlus: visibIsPlus(o['visib']),
      ceilingFt: ceiling,
      wx: typeof o['wxString'] === 'string' ? o['wxString'] : '',
    });
  }
  return out;
}

/* ---------------- where a neighbour lies ---------------- */

/** The eight points a distance is read with. Translated; `NO` is not `NE`. */
export type CompassPoint = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';

const POINTS: readonly CompassPoint[] = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];

/**
 * A true bearing → one of eight points. Eight and not sixteen: "21 NM NW" is a
 * sentence a pilot reads at a glance, and "21 NM WNW" is a number they check.
 */
export function compassPoint(deg: number): CompassPoint {
  const n = ((deg % 360) + 360) % 360;
  return POINTS[Math.round(n / 45) % 8] ?? 'n';
}

/**
 * Where a plan's aerodrome is, for the nearby lookup — openAIP first, the seed
 * table second.
 *
 * openAIP is the authority and is usually already in this tab's cache: the geo feed
 * resolves `von` / `nach` / `alt_name` on every route change, so the common case is
 * a cache read and no request at all. `AIRFIELDS` is the offline fallback and is
 * good enough for the question being asked — an aerodrome reference point half a
 * mile out does not change which station 24 NM away is the nearest one.
 */
export async function stationLatLon(icao: string): Promise<{ lat: number; lon: number } | null> {
  const id = icao.trim().toUpperCase();
  if (!ICAO_RE.test(id)) return null;
  const seed = AIRFIELDS[id];
  try {
    const f = await airportLatLon(id);
    if (f) return f;
  } catch {
    // a lookup that failed is not an answer; the seed table may still have one
  }
  return seed ? { lat: seed[0], lon: seed[1] } : null;
}

/* ---------------- the status line ---------------- */

/**
 * The status line's parts, ready to be translated and joined:
 * "METAR LJLJ 10:20Z · TAF gültig bis 18Z · aktualisiert 10:31Z". WHO published it
 * is deliberately not among them — that is the ⓘ's job.
 *
 * The TAF named is the one that runs out FIRST — with two aerodromes it is the
 * earlier end of validity that limits the flight, and printing every one of them
 * would bury that under punctuation.
 */
export interface WxStatusView {
  metars: { id: string; time: string }[];
  taf: { id: string; until: string } | null;
  /** how many stations have one — with a single TAF the line need not name it */
  tafCount: number;
  /**
   * "kein METAR für LJCE · nächste Meldung LJMB 24 NM NO" — one entry per plan
   * station that publishes nothing. It is part of the STATUS and not of the fetch
   * because the shared read-only view has only the status: a plan that says
   * "kein METAR für LJCE" and stops there has told the reader a problem and not an
   * answer.
   */
  nearby: { for: string; id: string; name: string; dist: number; dir: CompassPoint }[];
  fetched: string;
  source: string;
}

export function statusView(s: WxStatus | null | undefined): WxStatusView | null {
  if (!s) return null;
  const metars = s.stations
    .filter(st => st.obs_time)
    .map(st => ({ id: st.id, time: zTime(st.obs_time) }));
  let taf: { id: string; until: string; at: number } | null = null;
  let tafCount = 0;
  for (const st of s.stations) {
    const d = toDate(st.taf_valid_to);
    if (!d) continue;
    tafCount++;
    if (!taf || d.getTime() < taf.at) {
      taf = { id: st.id, until: zHour(st.taf_valid_to), at: d.getTime() };
    }
  }
  return {
    metars,
    taf: taf ? { id: taf.id, until: taf.until } : null,
    tafCount,
    nearby: (s.nearby ?? []).map(n => ({
      for: n.for,
      id: n.id,
      name: n.name ?? '',
      dist: Math.round(n.dist_nm),
      dir: compassPoint(n.bearing_deg),
    })),
    fetched: zTime(s.fetched_at),
    source: s.source ?? WX_SOURCE,
  };
}

/* ---------------- one station, decoded ---------------- */

/** One station's parsed observation, plus the raw texts the pilot may want to read. */
export interface WxParsed {
  id: string;
  /**
   * What the row's Station/Frequenz cell must say when this observation is written
   * into it.
   *
   * Absent for a plan's own aerodromes — the row keeps whatever already names it,
   * "LJLJ ATIS 128.175" included. Present, and then ALWAYS written, for a
   * NEIGHBOUR's report ("LJLJ (24 NM von LJCE)"): that cell is the only thing on
   * the printed page that says the observation is not this aerodrome's, and a
   * value it declined to overwrite would take the sentence off the paper.
   */
  station?: string;
  atis: AtisRow | null;
  metarRaw: string;
  tafRaw: string;
  obsTime: string | null;
  tafValidTo: string | null;
  /** the observation's flight category — NOAA's own `fltCat` wherever it published one */
  category: FlightCategory | null;
  /** the TAF's decoded periods, each with the category the coloured dot shows */
  periods: TafPeriod[];
  /** the refresh failed upstream — whatever is here is cache, or nothing */
  unavailable: boolean;
}

/** A station that really has an observation — the only kind that fills a row. */
interface WxObserved extends WxParsed {
  atis: AtisRow;
}

const observed = (p: WxParsed): p is WxObserved => p.atis !== null;

export function parseStation(s: WxStation): WxParsed {
  return {
    id: (s.id ?? '').toUpperCase(),
    atis: s.metar ? parseMetarToAtis(s.metar.raw) : null,
    metarRaw: s.metar?.raw ?? '',
    tafRaw: s.taf?.raw ?? '',
    obsTime: s.metar?.obs_time ?? null,
    tafValidTo: s.taf?.valid_to ?? null,
    category: metarCategory(s.metar),
    periods: tafPeriods(s.taf?.fcsts),
    unavailable: s.error === 'unavailable',
  };
}

/* ---------------- the ATIS rows ---------------- */

export interface AtisFill {
  /** field id → value; '' means "this cell has nothing to say any more" */
  values: Map<string, string>;
  /** which ATIS row each station was written into */
  rows: { id: string; row: number }[];
  /** stations that have a METAR but found no free row on the paper form */
  noRoom: string[];
}

/**
 * Which station a Station/Frequenz cell names: its FIRST word.
 *
 * The cell is not just an ICAO code — the openAIP feed writes "LJLJ ATIS 128.175"
 * into it (`geo.ts`), which is what the paper form's caption asks for. Reading
 * only the leading token is what lets a METAR recognise its own row afterwards.
 */
const stationOf = (cell: string): string => (cell.trim().split(/\s+/)[0] ?? '').toUpperCase();

/**
 * Place observations into the form's ATIS rows.
 *
 * A station already named in a row keeps that row — reloading updates the LJLJ
 * line in place instead of shuffling the block. Everything else takes the first
 * row that is free, where free means empty or still owned by an earlier fetch: a
 * row the pilot filled in by hand from a real ATIS broadcast (with its code
 * letter, runway and transition level, none of which a METAR has) is never
 * overwritten.
 *
 * The station cell of a row that ALREADY names this station is left exactly as it
 * stands: overwriting "LJLJ ATIS 128.175" with a bare "LJLJ" would throw away the
 * frequency to answer a question nobody asked.
 *
 * `rows` is the set of rows this call may write — the per-row ⟳ passes exactly
 * one, so refreshing LJMB in row 2 cannot make it jump up into row 1.
 */
export function atisFill(
  parsed: readonly WxParsed[],
  stationAt: (row: number) => string,
  ownedAt: (row: number) => boolean,
  rows: readonly number[] = ATIS_ROWS,
): AtisFill {
  const values = new Map<string, string>();
  const out: { id: string; row: number }[] = [];
  const noRoom: string[] = [];
  const taken = new Set<number>();
  const withMetar = parsed.filter(observed);
  const placed = new Map<string, number>();

  for (const p of withMetar) {
    const own = rows.find(r => !taken.has(r) && stationOf(stationAt(r)) === p.id);
    if (own === undefined) continue;
    taken.add(own);
    placed.set(p.id, own);
  }
  for (const p of withMetar) {
    if (placed.has(p.id)) continue;
    const free = rows.find(r => !taken.has(r) && (stationAt(r).trim() === '' || ownedAt(r)));
    if (free === undefined) {
      noRoom.push(p.id);
      continue;
    }
    taken.add(free);
    placed.set(p.id, free);
  }

  for (const p of withMetar) {
    const row = placed.get(p.id);
    if (row === undefined) continue;
    out.push({ id: p.id, row });
    const named = stationOf(stationAt(row)) === p.id;
    for (const key of ATIS_CELLS) {
      if (key === 'station') {
        if (p.station !== undefined) values.set(atisField(row, key), p.station);
        else if (!named) values.set(atisField(row, key), p.id);
        continue;
      }
      values.set(atisField(row, key), p.atis[key] ?? '');
    }
  }
  return { values, rows: out, noRoom };
}

/* ---------------- the Wetter-Beratung lines ---------------- */

const tidy = (s: string): string => s.replace(/\s+/g, ' ').trim();

/**
 * One V6 line: `TAF LJLJ …` / `METAR LJLJ …`.
 *
 * A bulletin that already carries its own header is written VERBATIM — that is
 * the text the pilot briefed on, and re-typing it around a template is how a
 * transcription error gets into a flight plan. `SPECI LJLJ …` therefore stays a
 * SPECI. Only a raw that begins with neither the keyword nor the station (which
 * happens) gets ours put in front of it.
 */
export function briefingLine(kind: 'TAF' | 'METAR', id: string, raw: string): string {
  const s = tidy(raw);
  const head = s.split(' ').slice(0, 2).join(' ').toUpperCase();
  if (head === kind + ' ' + id) return s;
  if (kind === 'METAR' && head === 'SPECI ' + id) return s;
  const body = s.toUpperCase().startsWith(id + ' ') ? s.slice(id.length + 1) : s;
  return tidy(kind + ' ' + id + ' ' + body);
}

/** The lines a briefing contributes, in the order they are offered a slot. */
export function wetterLinesFor(parsed: readonly WxParsed[]): string[] {
  const out: string[] = [];
  for (const p of parsed) {
    if (p.tafRaw) out.push(briefingLine('TAF', p.id, p.tafRaw));
    if (p.metarRaw) out.push(briefingLine('METAR', p.id, p.metarRaw));
  }
  return out;
}

/** "TAF LJLJ" / "METAR LJMB": what makes two lines the same briefing item. A SPECI
 *  replaces the station's METAR line — it IS the station's current observation. */
export function lineKey(s: string): string {
  const w = tidy(s).toUpperCase().split(' ');
  const kind = w[0] === 'SPECI' ? 'METAR' : w[0] ?? '';
  return kind + ' ' + (w[1] ?? '');
}

export interface WetterPush {
  /** field id → line text */
  values: Map<string, string>;
  /** lines that already stood there — nothing was written for them */
  kept: string[];
  /** lines that found no free row; the pilot has to make room */
  dropped: string[];
}

/**
 * Fit briefing lines into the six V6 rows.
 *
 * Three rules, in order: a line already standing there is left alone; a line that
 * replaces an earlier fetch's line for the same product and station overwrites
 * exactly that row (so a reload refreshes rather than accumulates); anything else
 * takes the first EMPTY row. Rows the pilot wrote are never touched, and when the
 * block is full the leftovers are reported rather than dropped in silence.
 */
export function pushWetterLines(
  current: readonly string[],
  owned: readonly boolean[],
  lines: readonly string[],
): WetterPush {
  const slots = current.map(s => s.trim());
  const mine = slots.map((_, i) => owned[i] === true);
  const values = new Map<string, string>();
  const kept: string[] = [];
  const dropped: string[] = [];

  for (const raw of lines) {
    const line = tidy(raw);
    if (line === '') continue;
    if (slots.some(s => s === line)) {
      kept.push(line);
      continue;
    }
    const key = lineKey(line);
    let at = slots.findIndex((s, i) => mine[i] === true && s !== '' && lineKey(s) === key);
    if (at < 0) at = slots.findIndex(s => s === '');
    if (at < 0) {
      dropped.push(line);
      continue;
    }
    slots[at] = line;
    mine[at] = true;
    values.set(wetterField(at + 1), line);
  }
  return { values, kept, dropped };
}

/* ---------------- the network step ---------------- */

export interface WxLoad {
  parsed: WxParsed[];
  source: string;
  fetched_at: string;
  status: WxStatus;
  /** which ATIS row each station landed in */
  rows: { id: string; row: number }[];
  /** asked for, but NOAA publishes no METAR for it (see {@link briefingFor}) */
  missing: string[];
  /** has a METAR, but the paper form's two ATIS rows were already spoken for */
  noRoom: string[];
  /** the refresh failed upstream for these */
  unavailable: string[];
}

/**
 * Fetch the stations and fill the ATIS block.
 *
 * `rows` narrows it to part of the form (the per-row ⟳); everything else is left
 * exactly as it is, status line included.
 */
export async function loadWeather(
  store: PlanStore,
  ids: readonly string[],
  opts: { rows?: readonly number[]; signal?: AbortSignal } = {},
): Promise<WxLoad> {
  const res: WxResponse = await fetchMetar([...ids], opts.signal);
  const parsed = (res.stations ?? []).map(parseStation);
  const rows = opts.rows ?? ATIS_ROWS;

  const fill = atisFill(
    parsed,
    row => store.val(atisField(row, 'station')),
    // "free" also covers a row the openAIP feed named ("LJLJ ATIS 128.175"): that is
    // not a pilot's hand-written broadcast either, and the two sources describe the
    // same aerodrome from two directions
    row => {
      const src = store.derivedSource(atisField(row, 'station'));
      return src === 'wx' || src === 'geo';
    },
    rows,
  );

  const fresh: WxStatus = {
    fetched_at: res.fetched_at,
    source: res.source || WX_SOURCE,
    stations: parsed.map(stationStatus),
  };
  // a per-row refresh must not throw away what the other rows were briefed on
  const status = opts.rows ? mergeStatus(store.wxStatus(), fresh) : fresh;

  store.setWx(fill.values, status);

  return {
    parsed,
    source: status.source ?? WX_SOURCE,
    fetched_at: res.fetched_at,
    status,
    rows: fill.rows,
    missing: parsed.filter(p => !p.atis && !p.unavailable).map(p => p.id),
    noRoom: fill.noRoom,
    unavailable: parsed.filter(p => p.unavailable).map(p => p.id),
  };
}

const stationStatus = (p: WxParsed): WxStationStatus => ({
  id: p.id,
  ...(p.obsTime ? { obs_time: p.obsTime } : {}),
  ...(p.tafValidTo ? { taf_valid_to: p.tafValidTo } : {}),
});

/**
 * The newer status wins per station; stations it does not mention keep theirs.
 *
 * The nearby list merges on the station it STANDS IN FOR (`for`), not on the
 * neighbour's id: a per-row refresh of LJCE may well find a different neighbour than
 * last time, and two entries for LJCE would print the fallback twice.
 */
export function mergeStatus(prev: WxStatus | null, next: WxStatus): WxStatus {
  const fresh = new Set(next.stations.map(s => s.id));
  const stations = [...next.stations];
  for (const s of prev?.stations ?? []) if (!fresh.has(s.id)) stations.push(s);
  const answered = new Set(next.stations.map(s => s.id));
  const nearby = [...(next.nearby ?? [])];
  const seen = new Set(nearby.map(n => n.for));
  for (const n of prev?.nearby ?? []) {
    // a station this fetch DID answer for has no fallback any more, whatever the
    // last one found — the aerodrome started reporting, or the pilot fixed a typo
    if (seen.has(n.for) || answered.has(n.for)) continue;
    nearby.push(n);
  }
  return { ...next, stations, ...(nearby.length ? { nearby } : {}) };
}

/* ---------------- the nearest station that does report ---------------- */

/**
 * One neighbour, decoded, and which of the plan's silent aerodromes it stands in for.
 *
 * `for` is never dropped and never merged into `id`. The two together are the whole
 * statement — "LJLJ's report, 24 NM from the LJCE you asked about" — and either one
 * alone is the misreading this feature exists to prevent.
 */
export interface NearbyFound {
  /** the plan station NOAA publishes nothing for */
  for: string;
  /** the neighbour's ICAO id */
  id: string;
  /** openAIP's name for it, or '' */
  name: string;
  distNm: number;
  bearingDeg: number;
  dir: CompassPoint;
  /** the neighbour's own report, decoded exactly as any other station's is */
  parsed: WxParsed;
  /**
   * Whether this neighbour may be written into an ATIS row at all.
   *
   * It may not when it is one of the plan's OWN aerodromes: that station already has
   * (or will have) its own row from the ordinary briefing, and writing it again would
   * relabel the departure's row as a substitute for the destination. The sentence is
   * still worth printing — "the nearest report to LJCE is your departure, 24 NM back"
   * is a fact — it is only the second cell that makes no sense.
   */
  takeable: boolean;
}

const nearbyStatus = (n: NearbyFound): WxNearbyStatus => ({
  for: n.for,
  id: n.id,
  ...(n.name ? { name: n.name } : {}),
  dist_nm: n.distNm,
  bearing_deg: n.bearingDeg,
  ...(n.parsed.obsTime ? { obs_time: n.parsed.obsTime } : {}),
  ...(n.parsed.tafValidTo ? { taf_valid_to: n.parsed.tafValidTo } : {}),
});

/**
 * For each aerodrome NOAA publishes nothing for, the nearest one it does.
 *
 * One request per silent station, in parallel and at most three of them — the ids
 * come from {@link WxLoad.missing}, which is bounded by the three cells a plan has.
 * A station whose coordinates cannot be resolved, or whose lookup fails, is simply
 * left without a fallback: one dead lookup must not lose the other two, and the
 * "kein METAR für …" sentence stands on its own as it did before.
 *
 * It writes NOTHING into the form. The result is a summary in `plans.meta.wx.nearby`
 * (so the shared read-only view can print the same sentence without fetching) and a
 * list for the panel to render. Writing a neighbour's observation into a free ATIS
 * row is a separate step, taken by the same unattended pass — see {@link takeNearby}.
 */
export async function loadNearby(
  store: PlanStore,
  ids: readonly string[],
  opts: { own?: readonly string[]; signal?: AbortSignal } = {},
): Promise<NearbyFound[]> {
  const own = new Set((opts.own ?? []).map(i => i.toUpperCase()));
  const results = await Promise.all(ids.map(id => oneNearby(id, own, opts.signal)));
  const found = results.filter((n): n is NearbyFound => n !== null);

  const prev = store.wxStatus();
  if (prev) {
    // the lookup answers for exactly the stations it was asked about; anything the
    // last one found for a station this call did not ask about stays
    const asked = new Set(ids.map(i => i.toUpperCase()));
    const kept = (prev.nearby ?? []).filter(n => !asked.has(n.for));
    const nearby = [...kept, ...found.map(nearbyStatus)];
    store.setWx(new Map(), { ...prev, ...(nearby.length ? { nearby } : {}) });
  }
  return found;
}

async function oneNearby(
  forId: string,
  own: Set<string>,
  signal?: AbortSignal,
): Promise<NearbyFound | null> {
  const id = forId.toUpperCase();
  const at = await stationLatLon(id);
  if (!at) return null;
  let res;
  try {
    res = await fetchNearby(at.lat, at.lon, {
      radiusNm: NEARBY_RADIUS_NM,
      limit: NEARBY_LIMIT,
      ...(signal ? { signal } : {}),
    });
  } catch (e) {
    if (isAbort(e)) throw e;
    return null;
  }
  const hit = (res.stations ?? []).find(st => st.metar || st.taf);
  if (!hit) return null;
  return describeNearby(id, hit, own);
}

/** The pure half of {@link oneNearby} — one response row → one panel entry. */
export function describeNearby(
  forId: string,
  st: WxNearbyStation,
  own: Set<string> = new Set(),
): NearbyFound {
  const parsed = parseStation(st);
  return {
    for: forId.toUpperCase(),
    id: parsed.id,
    name: st.name ?? '',
    distNm: st.dist_nm,
    bearingDeg: st.bearing_deg,
    dir: compassPoint(st.bearing_deg),
    parsed,
    takeable: !own.has(parsed.id),
  };
}

/**
 * Which ATIS rows a nearby take may use.
 *
 * Tighter than the ordinary briefing's rule, on purpose. A row the briefing itself
 * owns is holding a station's live observation and taking it would delete a report
 * the pilot asked for — including the neighbour's own row, on a plan where that
 * aerodrome is also a departure or a destination. A row openAIP merely named
 * ("LJCE INFO 123.5") is holding a frequency and no weather, and is exactly where
 * this belongs. The third case is this take's own earlier row, which is refreshed in
 * place rather than doubled.
 *
 * **Whether a row carries an observation is asked of the OBSERVATION cells, not of
 * the station cell.** The two have different owners in the commonest state there is:
 * the openAIP feed writes "LJLJ ATIS 128.175" and keeps that cell, the briefing then
 * fills the same row's time, wind, QNH and keeps THOSE — and a rule that read only
 * the station cell would call that row free and delete a live METAR to put a
 * neighbour's in its place.
 */
export function nearbyRows(store: PlanStore, id: string): number[] {
  const carries = (r: number): boolean =>
    ATIS_CELLS.some(k => k !== 'station' && store.derivedSource(atisField(r, k)) === 'wx');
  return ATIS_ROWS.filter(r => {
    const cell = store.val(atisField(r, 'station')).trim();
    const src = store.derivedSource(atisField(r, 'station'));
    // this take's OWN earlier row, and not the same station's ordinary briefing row:
    // `LJLJ (31 NM von LJCE)` is the only thing that ever puts a parenthesis into a
    // station cell, so the label format identifies itself in either language
    if (src === 'wx' && cell.startsWith(id + ' (')) return true;
    if (carries(r)) return false;
    return cell === '' || src === 'geo';
  });
}

export interface NearbyTake {
  /** the row it went into, or 0 when the two ATIS rows were both spoken for */
  row: number;
  values: Map<string, string>;
}

/**
 * Put a neighbour's observation into a free ATIS row, named as a neighbour's.
 *
 * `label` is built by the caller because it is prose and therefore translated —
 * "LJLJ (24 NM von LJCE)" — and it is written at write time in the interface
 * language, exactly like a checkpoint's remark and for the same reason: the value is
 * printed onto the PDF and read on paper, where there is no renderer left.
 *
 * The label is written UNCONDITIONALLY, even over a station cell that already names
 * the same aerodrome. That cell is the only thing on the page that says the report is
 * not this aerodrome's own, and a rule that declined to overwrite would silently take
 * the sentence off the paper.
 */
export function takeNearby(store: PlanStore, found: NearbyFound, label: string): NearbyTake {
  if (!found.takeable || !found.parsed.atis) return { row: 0, values: new Map() };
  const rows = nearbyRows(store, found.id);
  const fill = atisFill(
    [{ ...found.parsed, station: label }],
    row => store.val(atisField(row, 'station')),
    // the rows were vetted above, which is a stricter test than this one
    () => true,
    rows,
  );
  const row = fill.rows[0]?.row ?? 0;
  if (row) store.setWx(fill.values);
  return { row, values: fill.values };
}

/**
 * Write the raw TAF and METAR of everything just loaded into the free
 * Wetter-Beratung lines. Returns what happened, so the pilot can be told when the
 * block had no room left.
 */
export function pushBriefing(store: PlanStore, parsed: readonly WxParsed[]): WetterPush {
  const { current, owned } = wetterState(store);
  const push = pushWetterLines(current, owned, wetterLinesFor(parsed));
  if (push.values.size) store.setWx(push.values);
  return push;
}

/** The six V6 rows as they stand, and which of them this source owns. */
function wetterState(store: PlanStore): { current: string[]; owned: boolean[] } {
  const current: string[] = [];
  const owned: boolean[] = [];
  for (let i = 1; i <= WETTER_LINES; i++) {
    current.push(store.val(wetterField(i)));
    owned.push(store.derivedSource(wetterField(i)) === 'wx');
  }
  return { current, owned };
}

/**
 * Which briefing lines an UNATTENDED pass may write.
 *
 * The briefing arrives by itself now, so the V6 block fills itself — once per
 * bulletin. `pushed` is what this tab has already written there; a line whose key
 * is in it is offered again only while a row of ours still holds that key, which
 * is what makes the ten-minute refresh update a TAF in place and what makes
 * DELETING one stick. Clearing a line is the pilot saying "not this one", and a
 * source that put it straight back would be arguing with them.
 */
export function autoWetterLines(
  lines: readonly string[],
  current: readonly string[],
  owned: readonly boolean[],
  pushed: ReadonlySet<string>,
): string[] {
  const held = new Set<string>();
  current.forEach((s, i) => {
    if (owned[i] === true && tidy(s) !== '') held.add(lineKey(s));
  });
  return lines.filter(l => !pushed.has(lineKey(l)) || held.has(lineKey(l)));
}

/**
 * The unattended half of {@link pushBriefing}: write what has not been written
 * (or refresh what still stands), and report the keys it wrote so the caller can
 * remember them for next time.
 */
export function pushBriefingAuto(
  store: PlanStore,
  parsed: readonly WxParsed[],
  pushed: ReadonlySet<string>,
): { push: WetterPush; keys: string[] } {
  const { current, owned } = wetterState(store);
  const lines = autoWetterLines(wetterLinesFor(parsed), current, owned, pushed);
  const push = pushWetterLines(current, owned, lines);
  if (push.values.size) store.setWx(push.values);
  return { push, keys: [...push.values.values()].map(lineKey) };
}

/**
 * What one unattended pass has already written down, for as long as the tab lives.
 *
 * Two sets, and both exist for the same reason: the briefing now repeats itself
 * every ten minutes, and a pass that wrote a line the pilot has since deleted
 * would put it straight back. A key in here is written ONCE; afterwards it is
 * only ever refreshed in the row it still stands in.
 */
export interface BriefingMemo {
  /** `TAF LJLJ` / `METAR LJMB` — V6 lines this tab wrote */
  lines: Set<string>;
  /** `LJCE/LJLJ` — neighbours this tab put into an ATIS row */
  near: Set<string>;
}

export const newBriefingMemo = (): BriefingMemo => ({ lines: new Set(), near: new Set() });

export interface BriefingPass {
  load: WxLoad;
  /** what went into the six Wetter-Beratung lines */
  push: WetterPush;
  /** the nearest station for each aerodrome that publishes nothing */
  nearby: NearbyFound[];
  /** neighbour id → the ATIS row it went into; 0 = the two rows were spoken for */
  taken: Record<string, number>;
  /** the fallback failed; the briefing itself did not */
  nearbyError: unknown;
}

/**
 * The whole unattended pass, in the order a pilot needs it: the briefing, the raw
 * bulletins into the V6 block, and — for an aerodrome NOAA publishes nothing for —
 * the nearest station that does, written into a free ATIS row as somebody else's.
 *
 * `label` is the caller's, because the cell it fills is prose and therefore
 * translated („LJLJ (24 NM von LJCE)"); `onNearby` is called when the second half
 * starts, so the panel can say it is still looking. A failed fallback is REPORTED
 * and not thrown: the METARs are already on the form, and one dead box query must
 * not turn a good briefing into an error.
 */
export async function briefPlan(
  store: PlanStore,
  ids: readonly string[],
  memo: BriefingMemo,
  opts: { label(found: NearbyFound): string; signal?: AbortSignal; onNearby?(): void },
): Promise<BriefingPass> {
  const load = await loadWeather(store, ids, { ...(opts.signal ? { signal: opts.signal } : {}) });

  const { push, keys } = pushBriefingAuto(store, load.parsed, memo.lines);
  for (const k of keys) memo.lines.add(k);

  const pass: BriefingPass = { load, push, nearby: [], taken: {}, nearbyError: null };
  if (!load.missing.length) return pass;

  opts.onNearby?.();
  try {
    pass.nearby = await loadNearby(store, load.missing, {
      own: load.parsed.filter(p => p.atis).map(p => p.id),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (e) {
    if (isAbort(e)) throw e;
    pass.nearbyError = e;
    return pass;
  }
  for (const f of pass.nearby) {
    if (!f.takeable || !f.parsed.atis) continue;
    const mark = f.for + '/' + f.id;
    // written before and no longer on the form: the pilot cleared it, and it stays cleared
    if (memo.near.has(mark) && nearbyRowOf(store, f.id) === 0) continue;
    pass.taken[f.id] = takeNearby(store, f, opts.label(f)).row;
    memo.near.add(mark);
  }
  return pass;
}

/**
 * The ATIS row a neighbour's observation already stands in, or 0.
 *
 * The label format — `LJLJ (24 NM von LJCE)` — is the only thing that ever puts a
 * parenthesis into a station cell, in either language, so it identifies itself.
 * The unattended take asks this before writing: a row that still holds the
 * neighbour is refreshed, and a row the pilot cleared stays cleared.
 */
export function nearbyRowOf(store: PlanStore, id: string): number {
  for (const r of ATIS_ROWS) {
    if (store.derivedSource(atisField(r, 'station')) !== 'wx') continue;
    if (store.val(atisField(r, 'station')).trim().startsWith(id + ' (')) return r;
  }
  return 0;
}
